import type { paths } from '@luxuryescapes/contract-svc-lux-loyalty'
import requestUtils from 'api/requestUtils'
import { arrayToObject, nonNullable } from 'lib/array/arrayUtils'
import qs from 'qs'

type Response = paths['/api/lux-loyalty/program-configuration']['get']['responses']['200']['content']['application/json']
type ServerBenefit = Response['result']['benefits'][0]
type ServerTier = Response['result']['tiers'][0]

function mapTierBenefits(
  benefits: Array<ServerBenefit>,
  tier: App.LoyaltyTier,
): Array<App.LoyaltyRegionTierBenefit> {
  return nonNullable(benefits.map(benefit => {
    const isTierBenefitAvailable = benefit.tiers[tier].available
    if (!isTierBenefitAvailable) return
    return {
      code: benefit.code,
      limit: benefit.tiers[tier].limit === 'unlimited' ? undefined : benefit.tiers[tier].limit,
      unlimited: benefit.tiers[tier].limit === 'unlimited',
      value: benefit.tiers[tier].value,
    }
  }))
}

function mapTierThresholds(
  tiers: Array<ServerTier>,
): Record<App.LoyaltyTier, App.LoyaltyTierThreshold> {
  const thresholds = {} as Record<App.LoyaltyTier, App.LoyaltyTierThreshold>

  tiers.forEach(tier => {
    thresholds[tier.id] = {
      statusCredits: tier.statusCredits,
      nights: tier.nights,
    }
  })

  return thresholds
}

async function requestLoyaltyConfig(regionCode: string): Promise<App.LoyaltyConfig> {
  const query = qs.stringify({
    region: regionCode,
  })

  return requestUtils.get<Response>(
    `/api/lux-loyalty/program-configuration?${query}`,
    { excludeBrand: true },
  ).then((response): App.LoyaltyConfig => {
    const loungePassBenefit = response.result.benefits.find(benefit => benefit.code === 'lounge_pass')
    const transferBenefit = response.result.benefits.find(benefit => benefit.code === 'airport_transfer')
    const esimBenefit = response.result.benefits.find(benefit => benefit.code === 'esim_package')

    const benefitsLookup = arrayToObject(
      response.result.benefits,
      (benefit) => benefit.code,
      (benefit) => benefit,
    )

    return {
      loungePassExperienceIds: loungePassBenefit?.offerIds ?? [],
      transferAirports: transferBenefit?.eligibleAirports ?? [],
      benefits: response.result.benefits.map(benefit => ({
        code: benefit.code,
        paymentType: benefit.lePaymentType,
      })),
      regionEnabledBenefits: {
        airport_transfer: !!benefitsLookup.airport_transfer,
        discounted_insurance: !!benefitsLookup.discounted_insurance,
        early_access: !!benefitsLookup.early_access,
        esim_package: !!benefitsLookup.esim_package,
        hidden_access: !!benefitsLookup.hidden_access,
        lounge_pass: !!benefitsLookup.lounge_pass,
        phone_support: !!benefitsLookup.phone_support,
        tour_exp_credit: !!benefitsLookup.tour_exp_credit,
        travelshoot_credit: !!benefitsLookup.travelshoot_credit,
        hotel_upgrade: !!benefitsLookup.hotel_upgrade,
      },
      regionTierBenefits: {
        bronze: mapTierBenefits(response.result.benefits, 'bronze'),
        silver: mapTierBenefits(response.result.benefits, 'silver'),
        gold: mapTierBenefits(response.result.benefits, 'gold'),
        platinum: mapTierBenefits(response.result.benefits, 'platinum'),
      },
      tierThresholds: mapTierThresholds(response.result.tiers),
      esimExcludedCountryCodes: esimBenefit?.excludedCountryCodes ?? [],
    }
  })
}

export default requestLoyaltyConfig
